import { supabase } from '../auth/supabase-client';

/**
 * Push web côté navigateur. Trois règles tenues ici :
 *   - on ne demande jamais la permission à l'ouverture de l'application, mais
 *     seulement quand la personne appuie sur « Activer » dans Mon compte ;
 *   - l'abonnement est enregistré par une fonction Postgres, qui sait qui est
 *     connecté : le navigateur n'écrit pas dans la table ;
 *   - sur iPhone, tout cela n'existe que si l'application a été ajoutée à
 *     l'écran d'accueil. Le message le dit, plutôt que d'échouer sans raison.
 */

export type EtatPush = 'indisponible' | 'a-installer' | 'refuse' | 'inactif' | 'actif';

function client() {
  if (!supabase) throw new Error('Supabase indisponible.');
  return supabase;
}

/** Vrai quand le navigateur sait faire du push. */
export function pushSupporte(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * iOS n'expose le push qu'aux applications ajoutées à l'écran d'accueil. Safari
 * dans un onglet n'a ni `PushManager`, ni le droit de demander la permission.
 */
export function estIphoneHorsEcranAccueil(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!iOS) return false;
  const autonome =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  return !autonome;
}

export function etatPushLocal(abonne: boolean): EtatPush {
  if (estIphoneHorsEcranAccueil()) return 'a-installer';
  if (!pushSupporte()) return 'indisponible';
  if (Notification.permission === 'denied') return 'refuse';
  return abonne ? 'actif' : 'inactif';
}

/** La clé publique VAPID, telle que `pushManager.subscribe` l'attend. */
function versOctets(base64Url: string): Uint8Array {
  const complement = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + complement).replace(/-/g, '+').replace(/_/g, '/');
  const brut = atob(base64);
  const octets = new Uint8Array(brut.length);
  for (let index = 0; index < brut.length; index += 1) octets[index] = brut.charCodeAt(index);
  return octets;
}

async function clePublique(): Promise<string> {
  const { data, error } = (await client().functions.invoke('alba-push', {
    body: { action: 'cle-publique' },
  })) as { data: { cle?: string } | null; error: unknown };
  if (error || !data?.cle) throw new Error('Le push n’est pas configuré côté serveur.');
  return data.cle;
}

async function enregistrement(): Promise<ServiceWorkerRegistration> {
  const inscrit = await navigator.serviceWorker.getRegistration();
  if (inscrit) return inscrit;
  return navigator.serviceWorker.ready;
}

/** L'abonnement déjà pris par cet appareil, s'il existe. */
export async function abonnementCourant(): Promise<PushSubscription | null> {
  if (!pushSupporte()) return null;
  const registre = await navigator.serviceWorker.getRegistration();
  if (!registre) return null;
  return registre.pushManager.getSubscription();
}

/**
 * Demande la permission puis abonne l'appareil. À appeler depuis un vrai
 * appui : les navigateurs refusent la demande sortie de nulle part.
 */
export async function activerPush(espaceId: string): Promise<EtatPush> {
  if (estIphoneHorsEcranAccueil()) return 'a-installer';
  if (!pushSupporte()) return 'indisponible';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'refuse' : 'inactif';

  const cle = await clePublique();
  const registre = await enregistrement();
  const abonnement =
    (await registre.pushManager.getSubscription()) ??
    (await registre.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: versOctets(cle) as BufferSource,
    }));

  const brut = abonnement.toJSON() as { endpoint?: string; keys?: Record<string, string> };
  const { error } = await client().rpc('abonner_push', {
    p_workspace: espaceId,
    p_endpoint: brut.endpoint ?? abonnement.endpoint,
    p_keys: brut.keys ?? {},
    p_user_agent: navigator.userAgent,
  });
  if (error) throw error;
  return 'actif';
}

/** Coupe le push sur cet appareil seulement. */
export async function desactiverPush(espaceId: string): Promise<EtatPush> {
  const abonnement = await abonnementCourant();
  if (abonnement) {
    await client().rpc('desabonner_push', {
      p_workspace: espaceId,
      p_endpoint: abonnement.endpoint,
    });
    await abonnement.unsubscribe();
  }
  return etatPushLocal(false);
}

/** Une notification de contrôle sur les appareils de la personne connectée. */
export async function envoyerPushDeTest(): Promise<{ appareils: number; envoyes: number }> {
  const { data, error } = (await client().functions.invoke('alba-push', {
    body: { action: 'test' },
  })) as { data: { appareils?: number; envoyes?: number } | null; error: unknown };
  if (error) {
    const contexte = (error as { context?: Response }).context;
    if (contexte && typeof contexte.json === 'function') {
      try {
        const charge = (await contexte.json()) as { error?: string };
        if (charge.error) throw new Error(charge.error);
      } catch (cause) {
        if (cause instanceof Error && cause.message) throw cause;
      }
    }
    throw new Error('Le test n’a pas abouti.');
  }
  return { appareils: data?.appareils ?? 0, envoyes: data?.envoyes ?? 0 };
}

/**
 * Demande au serveur de vider la file du push. Appelé après une action qui
 * prévient quelqu'un (relais, décision) : la personne d'en face reçoit sa
 * notification dans la seconde au lieu d'attendre le tour de cron. L'échec
 * n'est pas remonté à l'écran : le tour de cron rattrapera.
 */
export function declencherPush(): void {
  if (!supabase) return;
  void supabase.functions.invoke('alba-push', { body: { action: 'envoyer' } }).catch(() => {});
}
